import Cookies from 'js-cookie';
import { UserConsentInput, UserConsentsApi, UserPreferences } from '../apis/UserConsentsApi/UserConsentsApi.ts';

const COOKIE_CONSENT_KEY = 'cookieConsent';
const COOKIE_CONSENT_VERSION = '1.0';

export interface UserConsentData {
    essentialCookies: boolean;
    analyticsCookies: boolean;
    consentDate: string;
    consentVersion: string;
}

export class CookieConsentService {
    static getConsent(): UserConsentData | null {
        const consentCookie = Cookies.get(COOKIE_CONSENT_KEY);

        if (!consentCookie)
            return null;

        try {
            return JSON.parse(consentCookie) as UserConsentData;
        } catch {
            return null;
        }
    }

    static hasConsent(category: 'essential' | 'analytics'): boolean {
        const consent = this.getConsent();
        if (!consent)
            return category === 'essential'; // Essential cookies are always allowed

        switch (category) {
            case 'essential':
                return consent.essentialCookies;
            case 'analytics':
                return consent.analyticsCookies;
            default:
                return false;
        }
    }

    static async saveConsent(consent: UserConsentInput): Promise<void> {
        const consentData: UserConsentInput = {
            ...consent,
            consentVersion: COOKIE_CONSENT_VERSION,
        };

        // Save to database first
        try {
            await UserConsentsApi.saveConsent(consentData);

            // After successful save, update local cookie
            const cookieData: UserConsentData = {
                essentialCookies: consentData.essentialCookies,
                analyticsCookies: consentData.analyticsCookies,
                consentDate: new Date().toISOString(),
                consentVersion: consentData.consentVersion || COOKIE_CONSENT_VERSION,
            };

            Cookies.set(COOKIE_CONSENT_KEY, JSON.stringify(cookieData), {
                expires: 365,
                sameSite: 'strict',
            });
        } catch (error) {
            console.error('Failed to save consent:', error);
            throw error;
        }
    }

    static async revokeConsent(): Promise<void> {
        // Revoke in API first
        try {
            await UserConsentsApi.revokeConsent();

            // Remove cookie after successful revocation
            Cookies.remove(COOKIE_CONSENT_KEY);
        } catch (error) {
            console.error('Failed to revoke consent in API:', error);
            throw error;
        }
    }

    static async syncWithApi(): Promise<UserPreferences | null> {
        try {
            // Always check database for user's consent
            const preferences = await UserConsentsApi.getPreferences();

            if (preferences.hasConsent && preferences.consentDate) {
                // Check if consent is expired (more than 1 year old)
                const isExpired = this.isConsentExpired(preferences.consentDate);

                if (isExpired) {
                    // Consent expired, delete cookie to force re-consent
                    Cookies.remove(COOKIE_CONSENT_KEY);
                } else {
                    // User has valid consent in database, update cookie
                    const cookieData: UserConsentData = {
                        essentialCookies: preferences.essentialCookies,
                        analyticsCookies: preferences.analyticsCookies,
                        consentDate: preferences.consentDate || new Date().toISOString(),
                        consentVersion: preferences.consentVersion || COOKIE_CONSENT_VERSION,
                    };

                    Cookies.set(COOKIE_CONSENT_KEY, JSON.stringify(cookieData), {
                        expires: 365,
                        sameSite: 'strict',
                    });
                }
            } else {
                // User has no consent in database or consent expired, delete cookie if it exists
                Cookies.remove(COOKIE_CONSENT_KEY);
            }

            return preferences;
        } catch (error) {
            console.error('Failed to sync with API:', error);
            // On error, delete cookie to be safe
            Cookies.remove(COOKIE_CONSENT_KEY);
            return null;
        }
    }

    static isConsentExpired(consentDate: string): boolean {
        if (!consentDate)
            return true;

        try {
            const consent = new Date(consentDate);
            const now = new Date();
            const diffTime = now.getTime() - consent.getTime();
            const diffDays = diffTime / (1000 * 60 * 60 * 24); // Convert to days

            // Consent expires after 1 year (365 days)
            return diffDays > 365;
        } catch {
            return true; // If we can't parse the date, consider it expired
        }
    }

    static needsConsent(): boolean {
        const consent = this.getConsent();

        // No consent at all
        if (!consent)
            return true;

        // Check if consent is expired (more than 1 year old)
        if (this.isConsentExpired(consent.consentDate))
            return true;

        return false;
    }

    static async acceptMMEWarning(): Promise<void> {
        // Accept MME warning in API
        try {
            await UserConsentsApi.patchMMEWarning({ accepted: true });
        } catch (error) {
            console.error('Failed to accept MME warning in API:', error);
            throw error;
        }
    }
}
